import classNames from 'classnames'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'

import { Translation } from 'cng-common/features/translations'
import { ITooltipProps, Tooltip } from 'core-components/library/tooltip'
import {
  calculateCockpitGrowth,
  calculateCockpitSobGrowth,
} from 'partner360/features/financialCockpit/lib/calculateCockpitGrowth'
import { getYearParams } from 'partner360/features/financialCockpit/ui/cockpitRevenueAndSob/lib/metricPresentation'
import {
  getHierarchyDimensionColumns,
  ICockpitPartnerLinkParams,
  IHierarchyControls,
  IHierarchyExportColumn,
  isExpandableRow,
  renderHierarchyValue,
  withZeroAsEmpty,
} from 'partner360/features/financialCockpit/ui/components/cockpitHierarchyTable'

import type { ICockpitExportCell } from '../../../../../api'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../../configs/translations'
import {
  EMPTY_COCKPIT_VALUE,
  formatCockpitPercent,
  formatCockpitPercentChange,
  getAchievementScoreBand,
  IExpectedExitQuarters,
  IScoredRevenueAndSobHierarchyRow,
  toBadgeScore,
  TRevenueAndSobHierarchyOrder,
} from '../../../../../lib'
import { CockpitAchievementScore } from '../../../../components/cockpitAchievementScore'
import { CockpitTableColumnTitle } from '../../../../components/cockpitTableTitle'
import { getDeltaTone, isEmptyMetricValue } from '../../../lib/metricPresentation'
import { getExpectedExitHint } from '../../cockpitExpectedExitHint/getExpectedExitHint'

import styles from './getRevenueAndSobTableColumns.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.revenueAndSob
const SCORE_KEY = FINANCIAL_COCKPIT_TRANSLATION_KEYS.achievementScore.score

interface IParams {
  fYear: number
  formatMoney: (value: number | null) => string
  showQuota: boolean
  isPeriodOpen: boolean
  /** Which selected quarters the expected exit reads as actuals and which as plan — what the
   *  Expected Exit headers' tooltips list. Only read while `isPeriodOpen`. */
  expectedExitQuarters: IExpectedExitQuarters
  /** Set only while the year before `fYear` is still in its last quarter
   *  (`selectIsCockpitPrevFYearClosing`): which of that year's quarters its expected exit reads as
   *  actuals and which as plan. Each group's baseline then reads as that year's YTD, that year's
   *  expected exit follows it, and `fYear`'s own Expected Exit/Actuals column is left out. */
  prevFYearExpectedExitQuarters?: IExpectedExitQuarters
  /** Whether the Geo column is shown at all — manager cockpit mode only. */
  showGeoHierarchy: boolean
  /** Which axis is outer. Meaningless when `showGeoHierarchy` is `false` — the caller pins this to
   *  `'productGeo'` in that case. */
  hierarchyOrder: TRevenueAndSobHierarchyOrder
  controls: IHierarchyControls
  /** Short display name for each geo `itemId` — what the Geo column labels its nodes with. */
  geoShortNameById: Map<string, string>
  /** Display name for each offering code — the only product level whose own code isn't already a
   *  `jbp-*` i18n key. */
  offeringNameByCode: Map<ProgramTypes, string>
  /** Display name for each partner id — what the dimension columns label their Partner leaves
   *  with. */
  partnerNameById: Map<string, string>
  /** Cockpit context each Partner leaf's link carries into the partner's own page. */
  partnerLinkParams: ICockpitPartnerLinkParams
  /** Resolves the i18n keys the header and the dimension cells show, for the export's plain-text
   *  copy of them. */
  translate: (key: string) => string
}

/** A value cell as the export reads it. Written beside the `renderFunction` it mirrors, and fed
 *  the very same formatted text and growth, so the deck cannot state a different number. The delta
 *  is left off on the same two counts `renderValueWithDelta` leaves it off — a zero growth, and a
 *  value the cell prints as `--` — so it never outlives the number it measures. Keep the two in
 *  step. */
const toValueExportCell = (
  text: string,
  value: number | null,
  growth: number | null = null,
  isSob = false
): ICockpitExportCell =>
  isEmptyMetricValue(value) || isEmptyMetricValue(growth)
    ? { text, value }
    : {
        text,
        value,
        delta: formatCockpitPercentChange(growth, isSob),
        deltaTone: getDeltaTone(growth),
      }

/** The Achievement circle's own text and band, read off the value `renderScore` hands the circle.
 *  A row with no score gets no circle there, only a bare `--`, and is sent the same way. */
const toScoreExportCell = (score: number | null): ICockpitExportCell => {
  if (score === null) {
    return { text: EMPTY_COCKPIT_VALUE, value: null }
  }

  const shown = toBadgeScore(score)

  return {
    text: String(shown),
    value: shown,
    badge: getAchievementScoreBand(shown) ?? undefined,
  }
}

/** The group header is the bare pillar title, sitting centered over its group's columns — hence
 *  `.groupTitle`, which `CockpitTableColumnTitle`'s own block layout doesn't give us. */
const renderGroupHeader = (title: string) => (
  <CockpitTableColumnTitle group className={styles.groupTitle}>
    <Translation text={title} span />
  </CockpitTableColumnTitle>
)

/** SCORE is the one value column whose title takes no `{fy}` param and no hint, so it skips the
 *  `.subHeader` wrapper `renderSubHeader` builds. */
const renderColumnHeader = (title: string) => (
  <CockpitTableColumnTitle>
    <Translation text={title} span />
  </CockpitTableColumnTitle>
)

/** A score cell always carries the circle: a row with nothing to score stands at the 0 it amounts
 *  to, rather than dropping to the `--` the value columns use. `getAchievementScoreBand` bands 0
 *  as `Low`, so it reads as the red circle a genuine zero does. */
const renderScore = (value: number | null) => {
  if (value === null) {
    return <span className={styles.emptyScore}>--</span>
  }

  return <CockpitAchievementScore score={toBadgeScore(value)} />
}

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6.75" stroke="currentColor" strokeWidth="1.2" />
    <path d="M8 7.25v4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <circle cx="8" cy="5.15" r="0.8" fill="currentColor" />
  </svg>
)

/** A value column's header, with an info icon beside the title when `renderHint` is given — the
 *  icon's tooltip draws whatever `renderHint` returns. */
const renderSubHeader = (
  title: string,
  replaceParams: ReturnType<typeof getYearParams>,
  renderHint?: ITooltipProps['tipContent']
) => (
  <CockpitTableColumnTitle>
    <span className={styles.subHeader}>
      <Translation text={title} replaceParams={replaceParams} span />
      {renderHint ? (
        <Tooltip tipContent={renderHint} inline>
          <span className={styles.hint}>
            <InfoIcon />
          </span>
        </Tooltip>
      ) : null}
    </span>
  </CockpitTableColumnTitle>
)

/** The value with its growth against baseline beside it. The delta is left off entirely — rather
 *  than shown as a `--` that would read as a missing value — on two counts: a zero growth is
 *  nothing to report, and a value the cell isn't showing has nothing to report about. The
 *  `withZeroAsEmpty` formatters print `--` for exactly the values `isEmptyMetricValue` rejects, so
 *  a delta can never outlive the number it measures. That is why the raw value arrives here
 *  alongside its formatter instead of pre-formatted. */
const renderValueWithDelta = (
  item: IScoredRevenueAndSobHierarchyRow,
  value: number | null,
  format: (value: number | null) => string,
  growth: number | null,
  isExpandable: boolean,
  isSob = false
) => {
  const showDelta = !isEmptyMetricValue(value) && !isEmptyMetricValue(growth)

  return (
    <span className={styles.cellValue}>
      {renderHierarchyValue(item, format(value), isExpandable)}
      {showDelta ? (
        <span className={classNames(styles.delta, styles[`delta--${getDeltaTone(growth)}`])}>
          {formatCockpitPercentChange(growth, isSob)}
        </span>
      ) : null}
    </span>
  )
}

export const getRevenueAndSobTableColumns = ({
  fYear,
  formatMoney,
  showQuota,
  isPeriodOpen,
  expectedExitQuarters,
  prevFYearExpectedExitQuarters,
  showGeoHierarchy,
  hierarchyOrder,
  controls,
  geoShortNameById,
  offeringNameByCode,
  partnerNameById,
  partnerLinkParams,
  translate,
}: IParams): IHierarchyExportColumn<IScoredRevenueAndSobHierarchyRow>[] => {
  const formatMoneyCell = withZeroAsEmpty(formatMoney)
  const formatPercentCell = withZeroAsEmpty(formatCockpitPercent)

  const baselineYearParams = getYearParams(fYear - 1)
  const yearParams = getYearParams(fYear)
  /** The header as it reads once its `{fy}` param is filled in — what `renderSubHeader` shows. */
  const headerText = (title: string, params: ReturnType<typeof getYearParams>) =>
    params.reduce((text, { key, value }) => text.replaceAll(key, value), translate(title))

  const isExpandable = (rowId: string) => isExpandableRow(controls, rowId)

  /** While the year before `fYear` is still in its last quarter, its baseline is only
   *  year-to-date, and where it is expected to exit stands beside it instead of `fYear`'s own
   *  expected exit. */
  const isPrevFYearClosing = prevFYearExpectedExitQuarters !== undefined
  const baselineTitle = isPrevFYearClosing ? KEYS.ytd : KEYS.baseline

  /** The Achievement scores ride along with the ACTUALS columns: `isPeriodOpen` is the same rule
   *  that decides whether the table shows Expected Exit or Actuals, and a score needs an actual to
   *  exist at all. */
  const showAchievementScore = !isPeriodOpen

  const salesExitOrActualsColumn: IHierarchyExportColumn<IScoredRevenueAndSobHierarchyRow> =
    isPeriodOpen
      ? {
          field: 'salesExpectedExit',
          parentField: 'salesGroup',
          title: KEYS.expectedExit,
          className: styles.valueCell,
          hideSettings: true,
          disableSort: true,
          hideSortIcon: true,
          hideSortIndex: true,
          hideFilterIcon: true,
          // The table's own header tooltip would repeat the title over the info icon's breakdown.
          tooltipVisible: false,
          titleRender: () =>
            renderSubHeader(
              KEYS.expectedExit,
              yearParams,
              getExpectedExitHint(expectedExitQuarters)
            ),
          exportTitle: headerText(KEYS.expectedExit, yearParams),
          renderFunction: (item) =>
            renderValueWithDelta(
              item,
              item.sales.expectedExit,
              formatMoneyCell,
              calculateCockpitGrowth({
                value: item.sales.expectedExit,
                baseline: item.sales.baseline,
              }),
              isExpandable(item.rowId)
            ),
          exportCell: (item) =>
            toValueExportCell(
              formatMoneyCell(item.sales.expectedExit),
              item.sales.expectedExit,
              calculateCockpitGrowth({
                value: item.sales.expectedExit,
                baseline: item.sales.baseline,
              })
            ),
        }
      : {
          field: 'salesActuals',
          parentField: 'salesGroup',
          title: KEYS.actuals,
          className: styles.valueCell,
          hideSettings: true,
          disableSort: true,
          hideSortIcon: true,
          hideSortIndex: true,
          hideFilterIcon: true,
          titleRender: () => renderSubHeader(KEYS.actuals, yearParams),
          exportTitle: headerText(KEYS.actuals, yearParams),
          renderFunction: (item) =>
            renderValueWithDelta(
              item,
              item.sales.actuals,
              formatMoneyCell,
              calculateCockpitGrowth({ value: item.sales.actuals, baseline: item.sales.baseline }),
              isExpandable(item.rowId)
            ),
          exportCell: (item) =>
            toValueExportCell(
              formatMoneyCell(item.sales.actuals),
              item.sales.actuals,
              calculateCockpitGrowth({ value: item.sales.actuals, baseline: item.sales.baseline })
            ),
        }

  const sobExitOrActualsColumn: IHierarchyExportColumn<IScoredRevenueAndSobHierarchyRow> =
    isPeriodOpen
      ? {
          field: 'sobExpectedExit',
          parentField: 'sobGroup',
          title: KEYS.expectedExit,
          className: styles.valueCell,
          hideSettings: true,
          disableSort: true,
          hideSortIcon: true,
          hideSortIndex: true,
          hideFilterIcon: true,
          // The table's own header tooltip would repeat the title over the info icon's breakdown.
          tooltipVisible: false,
          titleRender: () =>
            renderSubHeader(
              KEYS.expectedExit,
              yearParams,
              getExpectedExitHint(expectedExitQuarters)
            ),
          exportTitle: headerText(KEYS.expectedExit, yearParams),
          renderFunction: (item) =>
            renderValueWithDelta(
              item,
              item.sob.expectedExit,
              formatPercentCell,
              calculateCockpitSobGrowth({
                value: item.sob.expectedExit,
                baseline: item.sob.baseline,
              }),
              isExpandable(item.rowId),
              true
            ),
          exportCell: (item) =>
            toValueExportCell(
              formatPercentCell(item.sob.expectedExit),
              item.sob.expectedExit,
              calculateCockpitSobGrowth({
                value: item.sob.expectedExit,
                baseline: item.sob.baseline,
              }),
              true
            ),
        }
      : {
          field: 'sobActuals',
          parentField: 'sobGroup',
          title: KEYS.actuals,
          className: styles.valueCell,
          hideSettings: true,
          disableSort: true,
          hideSortIcon: true,
          hideSortIndex: true,
          hideFilterIcon: true,
          titleRender: () => renderSubHeader(KEYS.actuals, yearParams),
          exportTitle: headerText(KEYS.actuals, yearParams),
          renderFunction: (item) =>
            renderValueWithDelta(
              item,
              item.sob.actuals,
              formatPercentCell,
              calculateCockpitSobGrowth({ value: item.sob.actuals, baseline: item.sob.baseline }),
              isExpandable(item.rowId),
              true
            ),
          exportCell: (item) =>
            toValueExportCell(
              formatPercentCell(item.sob.actuals),
              item.sob.actuals,
              calculateCockpitSobGrowth({ value: item.sob.actuals, baseline: item.sob.baseline }),
              true
            ),
        }

  const prevFYearExpectedExitHint =
    prevFYearExpectedExitQuarters && getExpectedExitHint(prevFYearExpectedExitQuarters)

  /** Where the year before `fYear` is expected to exit, beside its YTD — only while that year is
   *  still in its last quarter. No delta, as the baseline beside it has none. */
  const salesPrevFYearExitColumn: IHierarchyExportColumn<IScoredRevenueAndSobHierarchyRow> = {
    field: 'salesPrevFYearExpectedExit',
    parentField: 'salesGroup',
    title: KEYS.expectedExit,
    className: styles.valueCell,
    hideSettings: true,
    disableSort: true,
    hideSortIcon: true,
    hideSortIndex: true,
    hideFilterIcon: true,
    // The table's own header tooltip would repeat the title over the info icon's breakdown.
    tooltipVisible: false,
    titleRender: () =>
      renderSubHeader(KEYS.expectedExit, baselineYearParams, prevFYearExpectedExitHint),
    exportTitle: headerText(KEYS.expectedExit, baselineYearParams),
    renderFunction: (item) =>
      renderHierarchyValue(
        item,
        formatMoneyCell(item.sales.prevFYearExpectedExit),
        isExpandable(item.rowId)
      ),
    exportCell: (item) =>
      toValueExportCell(
        formatMoneyCell(item.sales.prevFYearExpectedExit),
        item.sales.prevFYearExpectedExit
      ),
  }

  const sobPrevFYearExitColumn: IHierarchyExportColumn<IScoredRevenueAndSobHierarchyRow> = {
    field: 'sobPrevFYearExpectedExit',
    parentField: 'sobGroup',
    title: KEYS.expectedExit,
    className: styles.valueCell,
    hideSettings: true,
    disableSort: true,
    hideSortIcon: true,
    hideSortIndex: true,
    hideFilterIcon: true,
    // The table's own header tooltip would repeat the title over the info icon's breakdown.
    tooltipVisible: false,
    titleRender: () =>
      renderSubHeader(KEYS.expectedExit, baselineYearParams, prevFYearExpectedExitHint),
    exportTitle: headerText(KEYS.expectedExit, baselineYearParams),
    renderFunction: (item) =>
      renderHierarchyValue(
        item,
        formatPercentCell(item.sob.prevFYearExpectedExit),
        isExpandable(item.rowId)
      ),
    exportCell: (item) =>
      toValueExportCell(
        formatPercentCell(item.sob.prevFYearExpectedExit),
        item.sob.prevFYearExpectedExit
      ),
  }

  const salesQuotaColumn: IHierarchyExportColumn<IScoredRevenueAndSobHierarchyRow> = {
    field: 'salesQuota',
    parentField: 'salesGroup',
    title: KEYS.quota,
    className: styles.valueCell,
    hideSettings: true,
    disableSort: true,
    hideSortIcon: true,
    hideSortIndex: true,
    hideFilterIcon: true,
    titleRender: () => renderSubHeader(KEYS.quota, yearParams),
    exportTitle: headerText(KEYS.quota, yearParams),
    renderFunction: (item) =>
      renderValueWithDelta(
        item,
        item.sales.quota,
        formatMoneyCell,
        calculateCockpitGrowth({ value: item.sales.quota, baseline: item.sales.baseline }),
        isExpandable(item.rowId)
      ),
    exportCell: (item) =>
      toValueExportCell(
        formatMoneyCell(item.sales.quota),
        item.sales.quota,
        calculateCockpitGrowth({ value: item.sales.quota, baseline: item.sales.baseline })
      ),
  }

  const salesScoreColumn: IHierarchyExportColumn<IScoredRevenueAndSobHierarchyRow> = {
    field: 'salesScore',
    parentField: 'salesGroup',
    title: SCORE_KEY,
    className: styles.valueCell,
    hideSettings: true,
    disableSort: true,
    hideSortIcon: true,
    hideSortIndex: true,
    hideFilterIcon: true,
    titleRender: () => renderColumnHeader(SCORE_KEY),
    renderFunction: (item) => renderScore(item.salesScore),
    exportCell: (item) => toScoreExportCell(item.salesScore),
  }

  const sobScoreColumn: IHierarchyExportColumn<IScoredRevenueAndSobHierarchyRow> = {
    field: 'sobScore',
    parentField: 'sobGroup',
    title: SCORE_KEY,
    className: styles.valueCell,
    hideSettings: true,
    disableSort: true,
    hideSortIcon: true,
    hideSortIndex: true,
    hideFilterIcon: true,
    titleRender: () => renderColumnHeader(SCORE_KEY),
    renderFunction: (item) => renderScore(item.sobScore),
    exportCell: (item) => toScoreExportCell(item.sobScore),
  }

  return [
    ...getHierarchyDimensionColumns<IScoredRevenueAndSobHierarchyRow>({
      showGeoHierarchy,
      hierarchyOrder,
      controls,
      geoShortNameById,
      offeringNameByCode,
      partnerNameById,
      partnerLinkParams,
      translate,
    }),
    {
      field: 'salesGroup',
      title: KEYS.sales,
      className: classNames(styles.groupHeader, styles.groupStart, styles.parentHeader),
      hideSettings: true,
      disableSort: true,
      hideSortIcon: true,
      hideSortIndex: true,
      hideFilterIcon: true,
      titleRender: () => renderGroupHeader(KEYS.sales),
      exportTitle: translate(KEYS.sales),
    },
    {
      field: 'salesBaseline',
      parentField: 'salesGroup',
      title: baselineTitle,
      className: classNames(styles.valueCell, styles.groupStart),
      hideSettings: true,
      disableSort: true,
      hideSortIcon: true,
      hideSortIndex: true,
      hideFilterIcon: true,
      titleRender: () => renderSubHeader(baselineTitle, baselineYearParams),
      exportTitle: headerText(baselineTitle, baselineYearParams),
      renderFunction: (item) =>
        renderHierarchyValue(item, formatMoneyCell(item.sales.baseline), isExpandable(item.rowId)),
      exportCell: (item) =>
        toValueExportCell(formatMoneyCell(item.sales.baseline), item.sales.baseline),
    },
    ...(isPrevFYearClosing ? [salesPrevFYearExitColumn] : []),
    {
      field: 'salesPlan',
      parentField: 'salesGroup',
      title: KEYS.plan,
      className: styles.valueCell,
      hideSettings: true,
      disableSort: true,
      hideSortIcon: true,
      hideSortIndex: true,
      hideFilterIcon: true,
      titleRender: () => renderSubHeader(KEYS.plan, yearParams),
      exportTitle: headerText(KEYS.plan, yearParams),
      renderFunction: (item) =>
        renderValueWithDelta(
          item,
          item.sales.plan,
          formatMoneyCell,
          calculateCockpitGrowth({ value: item.sales.plan, baseline: item.sales.baseline }),
          isExpandable(item.rowId)
        ),
      exportCell: (item) =>
        toValueExportCell(
          formatMoneyCell(item.sales.plan),
          item.sales.plan,
          calculateCockpitGrowth({ value: item.sales.plan, baseline: item.sales.baseline })
        ),
    },
    ...(isPrevFYearClosing ? [] : [salesExitOrActualsColumn]),
    ...(showQuota ? [salesQuotaColumn] : []),
    ...(showAchievementScore ? [salesScoreColumn] : []),
    {
      field: 'sobGroup',
      title: KEYS.sob,
      className: classNames(styles.groupHeader, styles.groupStart, styles.parentHeader),
      hideSettings: true,
      disableSort: true,
      hideSortIcon: true,
      hideSortIndex: true,
      hideFilterIcon: true,
      titleRender: () => renderGroupHeader(KEYS.sob),
      exportTitle: translate(KEYS.sob),
    },
    {
      field: 'sobBaseline',
      parentField: 'sobGroup',
      title: baselineTitle,
      className: classNames(styles.valueCell, styles.groupStart),
      hideSettings: true,
      disableSort: true,
      hideSortIcon: true,
      hideSortIndex: true,
      hideFilterIcon: true,
      titleRender: () => renderSubHeader(baselineTitle, baselineYearParams),
      exportTitle: headerText(baselineTitle, baselineYearParams),
      renderFunction: (item) =>
        renderHierarchyValue(item, formatPercentCell(item.sob.baseline), isExpandable(item.rowId)),
      exportCell: (item) =>
        toValueExportCell(formatPercentCell(item.sob.baseline), item.sob.baseline),
    },
    ...(isPrevFYearClosing ? [sobPrevFYearExitColumn] : []),
    {
      field: 'sobPlan',
      parentField: 'sobGroup',
      title: KEYS.plan,
      className: styles.valueCell,
      hideSettings: true,
      disableSort: true,
      hideSortIcon: true,
      hideSortIndex: true,
      hideFilterIcon: true,
      titleRender: () => renderSubHeader(KEYS.plan, yearParams),
      exportTitle: headerText(KEYS.plan, yearParams),
      renderFunction: (item) =>
        renderValueWithDelta(
          item,
          item.sob.plan,
          formatPercentCell,
          calculateCockpitSobGrowth({ value: item.sob.plan, baseline: item.sob.baseline }),
          isExpandable(item.rowId),
          true
        ),
      exportCell: (item) =>
        toValueExportCell(
          formatPercentCell(item.sob.plan),
          item.sob.plan,
          calculateCockpitSobGrowth({ value: item.sob.plan, baseline: item.sob.baseline }),
          true
        ),
    },
    ...(isPrevFYearClosing ? [] : [sobExitOrActualsColumn]),
    ...(showAchievementScore ? [sobScoreColumn] : []),
  ]
}
